import {
  Box,
  Button,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import AppDialogDelete from "../../components/app-dialog-delete/AppDialogDelete";
import AppDialog from "../../components/app-dialog/AppDialog";
import AppTable from "../../components/app-table/AppTable";
import Iconify from "../../components/iconify/Iconify";
import {
  addDine,
  dineAction,
  dineDelete,
  getDineAreas,
  updateDine,
} from "../../reduxStore/slices/dineSlice";
import { useNotify } from "../../utils/snackbarUtils";
import "./DineAreasList.css";

function DineAreasList() {
  const tableHead = [
    { id: "name", label: "Dine Name", align: "left" },
    { id: "tables", label: "No. of Tables", align: "center" },
    { id: "status", label: "Status", align: "center" },
    { id: "createdAt", label: "Created At", align: "left" },
    { id: "actions", label: "Actions", align: "center", width: "120px" },
  ];

  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { areas, loading: isLoading } = useSelector((state) => state.dine);

  const storeId = user?.storeId;
  const tableRows = areas;

  // Local state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});
  const [dineName, setDineName] = useState("");
  const [aliasName, setAliasName] = useState("T");
  const [noOfTables, setNoOfTables] = useState(0);
  const [tablesArr, setTablesArr] = useState([]);
  const [deletedTables, setDeletedTables] = useState([]);
  const [viewTablesArr, setViewTablesArr] = useState([]);
  const [uid, setUid] = useState("");

  const handleDialogCancel = () => {
    setIsAddDialogOpen(false);
    setDineName("");
    setNoOfTables(0);
    setTablesArr([]);
    setAliasName("T");
    setUid("");
    setDeletedTables([]);
    setIsViewDialogOpen(false);
  };

  const handleDeleteClose = async () => {
    setIsDeleteDialogOpen(false);
  };

  const handleAddDine = () => {
    setIsAddDialogOpen(true);
  };

  const getAllDineAreas = async () => {
    try {
      await dispatch(getDineAreas({ storeId })).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleSaveDineAreas = async () => {
    if (!aliasName) {
      showError("Alias name can not be empty.");
      return;
    }
    try {
      const obj = {
        store_id: storeId,
        name: dineName,
        tables: tablesArr,
      };
      const res = await dispatch(addDine(obj)).unwrap();
      await getAllDineAreas();
      handleDialogCancel();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleUpdateDineAreas = async () => {
    if (!aliasName) {
      showError("Alias name can not be empty.");
      return;
    }
    try {
      const obj = {
        uid: selectedRecord?.uid,
        id: selectedRecord?.id,
        store_id: storeId,
        name: dineName,
        tables: [...tablesArr, ...deletedTables],
        record_status: "A",
      };
      const res = await dispatch(updateDine(obj)).unwrap();
      await getAllDineAreas();
      handleDialogCancel();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const toggleStatus = async (event, row) => {
    const obj = {
      store_id: storeId,
      uid: row.uid,
      id: row.id,
      record_status: event.target.value === "A" ? "D" : "A",
    };
    try {
      const res = await dispatch(dineAction(obj)).unwrap();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleteDialogOpen(false);
    const obj = {
      store_id: storeId,
      uid: selectedRecord.uid,
      id: selectedRecord.id,
      record_status: "R",
    };
    try {
      const res = await dispatch(dineDelete(obj)).unwrap();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleActionTable = (event, key) => {
    const updated = [...tablesArr];
    updated[key] = {
      ...updated[key],
      record_status: event.target.value === "A" ? "D" : "A",
    };
    setTablesArr(updated);
  };

  const handleUpdateTableChairs = (key, value) => {
    const updated = [...tablesArr];
    updated[key] = { ...updated[key], chairs: Number(value) };
    setTablesArr(updated);
  };

  const handleDeleteTable = (key, row) => {
    const updated = [...tablesArr];
    if (row?.uid && row?.uid !== 0) {
      setDeletedTables((prev) => [...prev, { ...row, record_status: "R" }]);
    }
    updated.splice(key, 1);
    setTablesArr(updated);
  };

  const editActionClick = async (event, row) => {
    setIsAddDialogOpen(true);
    setSelectedRecord(row);
    setUid(row?.uid);
    setDineName(row?.name);
    setNoOfTables(row?.tables?.length || 0);
    setTablesArr(row?.tables || []);
    setAliasName("T");
  };

  const deleteActionClick = async (event, row) => {
    setIsDeleteDialogOpen(true);
    setSelectedRecord(row);
  };

  const handleAddTable = () => {
    setTablesArr((prev) => [
      ...prev,
      {
        name: `${aliasName}${prev.length + 1}`,
        chairs: 4,
        is_new_record: true,
      },
    ]);
  };

  const cellClick = async (field_name, event, row) => {
    setIsViewDialogOpen(true);
    setViewTablesArr(row?.tables || []);
  };

  const handleDineAreaChange = (event) => {
    const value = event.target.value;
    const firstChar = value.charAt(0).toUpperCase();
    setAliasName(firstChar);
    setDineName(firstChar + value.slice(1));
  };

  // Load dine areas
  useEffect(() => {
    if (storeId) {
      getAllDineAreas();
    }
  }, [storeId]);

  // Auto-generate tables
  useEffect(() => {
    if (noOfTables > 0 && !uid) {
      const generated = [];
      for (let i = 1; i <= noOfTables; i++) {
        generated.push({
          name: `${aliasName}${i}`,
          chairs: tablesArr[i]?.chairs || 4,
          record_status: "A",
        });
      }
      setTablesArr(generated);
    } else {
      setTablesArr((prev) =>
        prev.map((item, i) => ({ ...item, name: `${aliasName}${i + 1}` }))
      );
    }
  }, [noOfTables, aliasName]);

  const dialogAddDineFooter = (
    <>
      <Box
        style={{
          display: "flex",
          gap: 15,
        }}
      >
        <Button size="medium" onClick={handleDialogCancel}>
          Cancel
        </Button>
        <AppButton
          disabled={isLoading}
          isLoading={isLoading}
          fullWidth
          size="medium"
          variant="contained"
          onClick={uid ? handleUpdateDineAreas : handleSaveDineAreas}
        >
          {uid ? "Update" : "Save"}
        </AppButton>
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        margin: "25px 60px",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Tooltip title="Back to Configuration">
            <IconButton onClick={() => navigate("/app/config")}>
              <Iconify icon="eva:arrow-back-fill" width={24} height={24} />
            </IconButton>
          </Tooltip>

          <Typography variant="h5" gutterBottom>
            Dine Areas & Tables
          </Typography>
        </Stack>
        <Link style={{ textDecoration: "none" }}>
          {tableRows.length > 0 && !isLoading && (
            <AppButton
              onClick={handleAddDine}
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              New Dine
            </AppButton>
          )}
        </Link>
      </Stack>
      <Box>
        <AppTable
          tableHead={tableHead}
          tableRows={tableRows}
          listType="dineTables"
          searchPlaceholder="Search by Dine Name"
          toggleSwitchStatus={toggleStatus}
          editActionClick={editActionClick}
          deleteActionClick={deleteActionClick}
          cellClick={cellClick}
          storeId={storeId}
          handleAddAction={handleAddDine}
          emptyTitle="No dine areas & tables available"
          emptyDesc="Add new dine to get started."
          emptyButtonLabel="New Dine"
          isLoading={isLoading}
        />
      </Box>

      {isAddDialogOpen && (
        <AppDialog
          maxWidth="sm"
          isOpen={isAddDialogOpen}
          id={"add_dine_dialog"}
          title={<>{uid ? "Edit Dine" : "Add Dine"}</>}
          footer={dialogAddDineFooter}
        >
          <Box>
            <Stack
              spacing={3}
              style={{
                marginTop: 30,
                marginBottom: 30,
              }}
            >
              <TextField
                autoFocus
                label="Dine area name*"
                fullWidth
                placeholder=""
                autoComplete="off"
                name="dine_area_name"
                value={dineName}
                onChange={(event) => {
                  handleDineAreaChange(event);
                }}
              />
              <TextField
                label="Short code*"
                fullWidth
                placeholder=""
                autoComplete="off"
                name="alias"
                value={aliasName}
                inputProps={{ maxLength: 1 }}
                onChange={(event) =>
                  setAliasName(event.target.value.toUpperCase())
                }
                helperText="Enter a short code (e.g., 'T') that identifies your tables."
              />
              <TextField
                type="number"
                label="No. of Tables*"
                fullWidth
                placeholder=""
                autoComplete="off"
                name="no_tables"
                disabled={uid !== "" ? true : false}
                value={noOfTables}
                onInput={(e) => {
                  e.target.value = Math.max(0, parseInt(e.target.value))
                    .toString()
                    .slice(0, 2);
                }}
                onChange={(event) => setNoOfTables(event.target.value)}
                helperText="Enter how many tables this dine area will have. Example: 5"
              />
              <Box>
                <Box className="dine-area-table-header">
                  <Box>Table Short code</Box>
                  <Box className="dine-area-no-chairs-cell">No. of Chairs</Box>
                  <Box className="dine-area-no-action-cell">Action</Box>
                </Box>
                <Box className="dine-area-table-body">
                  {tablesArr &&
                    tablesArr?.map((tbl, key) => {
                      return (
                        <Box key={tbl.name} className="dine-area-table-row">
                          <Box className="short-code-cell">{tbl.name}</Box>
                          <Box className="dine-area-no-chairs-cell">
                            <TextField
                              type="number"
                              size="small"
                              label="No. of chairs*"
                              autoComplete="off"
                              value={tbl.chairs}
                              onChange={(event) =>
                                handleUpdateTableChairs(key, event.target.value)
                              }
                            />
                          </Box>
                          <Box
                            style={{
                              display: "flex",
                              gap: 10,
                            }}
                          >
                            <Box>
                              <Switch
                                value={tbl?.record_status}
                                checked={
                                  tbl?.record_status === "A" ? true : false
                                }
                                onChange={(event) =>
                                  handleActionTable(event, key)
                                }
                              />
                            </Box>
                            {tablesArr.length - 1 === key ? (
                              <Box className="dine-area-no-action-cell">
                                <IconButton
                                  onClick={() => handleDeleteTable(key, tbl)}
                                >
                                  <Iconify icon={"eva:trash-2-outline"} />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box className="dine-area-no-action-cell"></Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  {uid && (
                    <Box
                      style={{
                        marginTop: 10,
                      }}
                    >
                      <AppButton
                        onClick={handleAddTable}
                        startIcon={<Iconify icon="eva:plus-fill" />}
                      >
                        Add Table
                      </AppButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Stack>
          </Box>
        </AppDialog>
      )}

      {isViewDialogOpen && (
        <AppDialog
          maxWidth="xs"
          isOpen={isViewDialogOpen}
          id={"view_dine_dialog"}
          title={"Table Info"}
          footer={
            <Box
              style={{
                display: "flex",
                gap: 15,
              }}
            >
              <Button size="medium" onClick={handleDialogCancel}>
                Close
              </Button>
            </Box>
          }
        >
          <Box>
            <Stack
              spacing={3}
              style={{
                marginTop: 30,
                marginBottom: 30,
              }}
            >
              <Box>
                <Box className="dine-area-table-header">
                  <Box>Table Short code</Box>
                  <Box>No. of Chairs</Box>
                  {/* <Box className='dine-area-no-action-cell'>Action</Box> */}
                </Box>
                <Box className="dine-area-table-body">
                  {viewTablesArr &&
                    viewTablesArr?.map((tbl, key) => {
                      return (
                        <Box key={tbl.name} className="dine-area-table-row">
                          <Box>{tbl.name}</Box>
                          <Box>{tbl.chairs}</Box>
                          {/* <Box className='dine-area-no-action-cell'>
                                                    <IconButton onClick={() => handleDownloadQr(key, tbl)}>
                                                        <Iconify icon={'material-symbols:qr-code'} />
                                                    </IconButton>
                                                </Box> */}
                        </Box>
                      );
                    })}
                </Box>
              </Box>
            </Stack>
          </Box>
        </AppDialog>
      )}

      {isDeleteDialogOpen && (
        <AppDialogDelete
          isOpen={isDeleteDialogOpen}
          id={"delete_dine_area_dialog"}
          title={<>Delete Dine Area</>}
          footer={
            <>
              <Button
                autoFocus
                onClick={handleDeleteConfirm}
                className="confirm-delete-btn"
              >
                Delete
              </Button>
              <Button
                onClick={handleDeleteClose}
                variant="outlined"
                className="cancel-delete-btn"
              >
                Cancel
              </Button>
            </>
          }
        >
          <Typography
            style={{
              fontSize: 14,
              color: "#000",
            }}
          >
            Are you sure want to delete record?
          </Typography>
        </AppDialogDelete>
      )}
    </Box>
  );
}

export default DineAreasList;
